// frontend/src/api/requests.ts
// Family Doctor Portal — Requests API client
// Uses same-origin fetch so SWA auth cookies/headers apply automatically.

export type RequestStatus =
  | "Submitted"
  | "InReview"
  | "Accepted"
  | "AttachmentComplete"
  | "Declined"
  | "Waitlist"
  | "Cancelled"
  | "Expired"
  | "ClosedPatientWithdrew"
  | "ClosedAcceptedElsewhere"
  | "ClosedNoShow"
  | "ClosedClinicDeclinedAfterMeetGreet"
  | string;

export type CoverageTypeRef = {
  id: number;
  name: string | null;
};

export type PriorityTag = {
  key: string;
  label: string;
  kind: "system" | string;
  basis?: string | null;
};

export type PatientIdentity = {
  leadId?: number;

  firstName?: string | null;
  lastName?: string | null;
  dateOfBirth?: string | null;

  postalCode?: string | null;

  // Patient gender from details endpoint.
  gender?: string | null;

  // Backward-compat: some older endpoints/rows might still provide name.
  name?: string | null;
  fullName?: string | null;
};

export type ApplicantContact = {
  email: string | null;
  phone: string | null;
};

export type RequestSourceScope = "public" | "navigator" | "all";

export type NavigatorRequestContext = {
  isNavigatorAssisted?: boolean | null;
  navigatorCaseId: number | null;
  sourceType: string | null;
  sourceLabel: string | null;
  priorityLevel?: string | null;
  publicSummary?: string | null;
  forwardedByNavigatorUserId: number | null;
  forwardedByNavigatorName: string | null;
  forwardedAt: string | null;
};

export type PatientRequestActivity = {
  id: number;
  activityType:
    | "PhoneCall"
    | "Email"
    | "PatientCalled"
    | "WorkflowUpdate"
    | string
    | null;
  activityTypeLabel?: string | null;
  outcomeCode: string | null;
  label: string | null;
  note: string | null;
  createdAt: string | null;
  createdByUserId: number | null;
  createdByRole: "FamilyDoctor" | "ClinicAdmin" | string | null;
  createdByName?: string | null;
  createdByEmail?: string | null;
  isSystemGenerated: boolean;
};

export type RequestsListItem = {
  requestId: number;
  status: RequestStatus;

  /**
   * Baseline patient status at the moment of request submission.
   * Derived from MOH reason selection (SortOrder 10–60 => unattached).
   */
  patientStatusBeforeRequest?: "attached" | "unattached" | null;

  // Safe system-derived request tags.
  // These are shared by public and Navigator requests and do not expose Navigator matching logic.
  priorityTags?: PriorityTag[];

  // Updated schema: patient identity is separate from applicant contact.
  patient: PatientIdentity;

  // Present on list endpoint after Jan 2026 enhancements.
  applicant?: ApplicantContact;

  request: {
    requestFor: string | null;
    source: string | null;

    // Navigator/source separation fields.
    sourceType?: string | null;
    sourceLabel?: string | null;

    // Coverage type: administrative / eligibility metadata.
    coverageType: CoverageTypeRef | null;
  };

  // Present only for navigator-forwarded requests.
  // Safe operational context only; no match score, match reasons, or cautions.
  navigator?: NavigatorRequestContext | null;

  timeline: {
    createdAt: string;
    submittedAt: string | null;
    verifiedAt?: string | null;
    decisionAt: string | null;
    cancelledAt: string | null;
    autoExpiredAt?: string | null;

    // Present when status is AttachmentComplete.
    attachmentCompletedAt?: string | null;

    // Navigator-forwarded request timestamp.
    forwardedAt?: string | null;
  };
};

export type RequestDetail = {
  requestId: number;
  status: RequestStatus;
  rawStatus?: RequestStatus;
  rawStatusName?: RequestStatus;

  // Safe system-derived request tags.
  // These are shared by public and Navigator requests and do not expose Navigator matching logic.
  priorityTags?: PriorityTag[];

  // Navigator priority level from the forwarded navigator context, when present.
  navigatorPriorityLevel?: string | null;

  patient: PatientIdentity;

  applicant?: ApplicantContact;

  request: {
    requestFor: string | null;
    source: string | null;

    // Navigator/source separation fields.
    sourceType?: string | null;
    sourceLabel?: string | null;

    // Coverage type: administrative / eligibility metadata.
    coverageType: CoverageTypeRef | null;

    otherReasonText: string | null;
  };

  // Present only for navigator-forwarded requests.
  // Safe operational context only; no match score, match reasons, or cautions.
  navigator?: NavigatorRequestContext | null;

  reasons: Array<{
    id: number;
    name: string;
  }>;

  // CRM / activity timeline entries from dbo.FamilyDoctorRegistrationRequestActivity.
  activities?: PatientRequestActivity[];

  timeline: {
    createdAt: string;
    submittedAt: string | null;

    // Latest decision time.
    decisionAt: string | null;

    // Terminal timestamps.
    cancelledAt: string | null;
    autoExpiredAt: string | null;

    // Present when status is AttachmentComplete.
    attachmentCompletedAt?: string | null;
    attachmentCompletedByUserId?: number | null;

    // Navigator-forwarded request timestamp.
    forwardedAt?: string | null;

    // CRM / activity timeline entries from dbo.FamilyDoctorRegistrationRequestActivity.
    activities?: PatientRequestActivity[];
  };
};

export type DecisionValue = "Accepted" | "Declined" | "Waitlist";

// Post-acceptance finalize actions.
export type FinalizeAction =
  | "AttachmentComplete"
  | "Declined"
  | "PatientWithdrew"
  | "PatientAcceptedElsewhere"
  | "NoShow"
  | "ClinicDeclinedAfterMeetAndGreet"
  | "MovedToWaitlist";

const ALLOWED_FINALIZE_ACTIONS: FinalizeAction[] = [
  "AttachmentComplete",
  "Declined",
  "PatientWithdrew",
  "PatientAcceptedElsewhere",
  "NoShow",
  "ClinicDeclinedAfterMeetAndGreet",
  "MovedToWaitlist",
];

// Shared UI status filter helper.
export type StatusFilter = "Active" | "All" | string;

export type ListFamilyDoctorRequestsOptions = {
  status?: StatusFilter | null;
  sourceScope?: RequestSourceScope | null;
};

export type DecisionEmailPreview = {
  success: boolean;
  preview: true;
  decision: DecisionValue;
  email: {
    to: string | null;
    subject: string;
    text: string;
    html?: string;
  };
};

export type FinalizeEmailPreview = {
  success: boolean;
  preview: true;
  action: FinalizeAction;
  targetStatus?: RequestStatus;
  message?: string;
  email: {
    to: string | null;
    subject: string;
    text: string;
    html?: string;
  };
};

export class ApiError extends Error {
  status: number;
  payload?: any;

  constructor(status: number, message: string, payload?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    // Important: allow SWA auth to flow via same-origin cookies/headers.
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      ...(init?.headers || {}),
    },
    ...init,
  });

  const text = await res.text();
  let data: any = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text || null;
  }

  if (!res.ok) {
    const message =
      (data && (data.error || data.message)) ||
      `Request failed (${res.status})`;
    throw new ApiError(res.status, message, data);
  }

  return data as T;
}

function normalizeStatusFilter(status?: StatusFilter | null): string | null {
  const raw = String(status || "").trim();
  if (!raw) return null;

  const k = raw.toLowerCase();
  if (k === "active") return null; // backend default active-only
  if (k === "all") return "All"; // backend expects All

  // specific status passthrough
  return raw;
}

function normalizeSourceScope(
  sourceScope?: RequestSourceScope | null
): RequestSourceScope {
  const raw = String(sourceScope || "public")
    .trim()
    .toLowerCase();

  if (raw === "navigator") return "navigator";
  if (raw === "all") return "all";

  return "public";
}

function buildRequestListQuery(params: {
  status?: string | null;
  sourceScope?: RequestSourceScope | null;
}): string {
  const searchParams = new URLSearchParams();

  const status = String(params.status || "").trim();
  if (status) {
    searchParams.set("status", status);
  }

  searchParams.set("sourceScope", normalizeSourceScope(params.sourceScope));

  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

/**
 * GET /api/portal/family-doctor/requests
 *
 * v1 semantics:
 * - listFamilyDoctorRequests() => ACTIVE public/direct requests only
 * - listFamilyDoctorRequests("Active") => ACTIVE public/direct requests only
 * - listFamilyDoctorRequests("All") => all verified public/direct requests
 * - listFamilyDoctorRequests("Accepted") => public/direct requests filtered by status
 * - listFamilyDoctorRequests({ sourceScope: "navigator" }) => ACTIVE navigator-forwarded requests
 * - listFamilyDoctorRequests({ status: "All", sourceScope: "navigator" }) => all verified navigator-forwarded requests
 */
export async function listFamilyDoctorRequests(
  statusOrOptions?: StatusFilter | ListFamilyDoctorRequestsOptions | null
): Promise<RequestsListItem[]> {
  const options: ListFamilyDoctorRequestsOptions =
    typeof statusOrOptions === "object" && statusOrOptions !== null
      ? statusOrOptions
      : { status: statusOrOptions || null };

  const normalizedStatus = normalizeStatusFilter(options.status || null);
  const url = `/api/portal/family-doctor/requests${buildRequestListQuery({
    status: normalizedStatus,
    sourceScope: options.sourceScope || "public",
  })}`;

  return requestJson<RequestsListItem[]>(url, { method: "GET" });
}

/**
 * Dashboard helper: load ALL verified public/direct requests, not Active-only.
 * This avoids missing Declined/Expired/AttachmentComplete outcomes in KPIs.
 */
export async function listFamilyDoctorRequestsAll(): Promise<
  RequestsListItem[]
> {
  return listFamilyDoctorRequests({ status: "All", sourceScope: "public" });
}

/**
 * Navigator Requests helper: load ACTIVE navigator-forwarded requests.
 */
export async function listFamilyDoctorNavigatorRequests(
  status?: StatusFilter | null
): Promise<RequestsListItem[]> {
  return listFamilyDoctorRequests({
    status: status || null,
    sourceScope: "navigator",
  });
}

/**
 * Navigator Requests dashboard/helper: load ALL verified navigator-forwarded requests.
 */
export async function listFamilyDoctorNavigatorRequestsAll(): Promise<
  RequestsListItem[]
> {
  return listFamilyDoctorRequests({
    status: "All",
    sourceScope: "navigator",
  });
}

/**
 * GET /api/portal/family-doctor/requests/{requestId}
 */
export async function getFamilyDoctorRequestDetails(
  requestId: number
): Promise<RequestDetail> {
  if (!Number.isFinite(requestId) || requestId <= 0) {
    throw new Error("Invalid requestId");
  }

  return requestJson<RequestDetail>(
    `/api/portal/family-doctor/requests/${requestId}`,
    { method: "GET" }
  );
}

/**
 * POST /api/portal/family-doctor/requests/{requestId}/decision
 * body: { decision: "Accepted" | "Declined" | "Waitlist", preview: true }
 *
 * This does NOT apply the decision.
 * It returns the exact email payload the backend will use when the doctor confirms.
 */
export async function previewFamilyDoctorRequestDecision(
  requestId: number,
  decision: DecisionValue
): Promise<DecisionEmailPreview> {
  if (!Number.isFinite(requestId) || requestId <= 0) {
    throw new Error("Invalid requestId");
  }

  return requestJson<DecisionEmailPreview>(
    `/api/portal/family-doctor/requests/${requestId}/decision`,
    {
      method: "POST",
      body: JSON.stringify({ decision, preview: true }),
    }
  );
}

/**
 * POST /api/portal/family-doctor/requests/{requestId}/decision
 * body: { decision: "Accepted" | "Declined" | "Waitlist" }
 *
 * This applies the decision and sends the patient email after doctor confirmation.
 */
export async function decideFamilyDoctorRequest(
  requestId: number,
  decision: DecisionValue
): Promise<{
  success: boolean;
  decision: DecisionValue;
  autoClosedCount?: number;
}> {
  if (!Number.isFinite(requestId) || requestId <= 0) {
    throw new Error("Invalid requestId");
  }

  return requestJson<{
    success: boolean;
    decision: DecisionValue;
    autoClosedCount?: number;
  }>(`/api/portal/family-doctor/requests/${requestId}/decision`, {
    method: "POST",
    body: JSON.stringify({ decision }),
  });
}

/**
 * POST /api/portal/family-doctor/requests/{requestId}/finalize
 * body: { action: FinalizeAction, note?: string, preview: true }
 *
 * This does NOT apply the finalize action.
 * It is for post-Meet-&-Greet / post-invitation outcome email previews.
 *
 * Important:
 * - Do not use previewFamilyDoctorRequestDecision(..., "Waitlist") for MovedToWaitlist.
 * - MovedToWaitlist is a finalize action, not an initial Waitlist decision.
 */
export async function previewFamilyDoctorRequestFinalize(
  requestId: number,
  action: FinalizeAction,
  note?: string | null
): Promise<FinalizeEmailPreview> {
  if (!Number.isFinite(requestId) || requestId <= 0) {
    throw new Error("Invalid requestId");
  }

  const a = String(action || "").trim() as FinalizeAction;
  if (!ALLOWED_FINALIZE_ACTIONS.includes(a)) {
    throw new Error("Invalid finalize action");
  }

  const cleanNote = String(note || "").trim();

  return requestJson<FinalizeEmailPreview>(
    `/api/portal/family-doctor/requests/${requestId}/finalize`,
    {
      method: "POST",
      body: JSON.stringify({
        action: a,
        preview: true,
        ...(cleanNote ? { note: cleanNote } : {}),
      }),
    }
  );
}

/**
 * POST /api/portal/family-doctor/requests/{requestId}/finalize
 *
 * Existing body values:
 * - { action: "AttachmentComplete", note?: string }
 * - { action: "Declined", note?: string }
 *
 * Additional backlog-clearing body values:
 * - { action: "PatientWithdrew", note?: string }
 * - { action: "PatientAcceptedElsewhere", note?: string }
 * - { action: "NoShow", note?: string }
 * - { action: "ClinicDeclinedAfterMeetAndGreet", note?: string }
 * - { action: "MovedToWaitlist", note?: string }
 *
 * Note:
 * - Only valid when request is in Accepted state, except idempotent terminal handling.
 * - AttachmentComplete does not send email.
 * - NoShow sends a patient email with a fresh request link.
 * - MovedToWaitlist sends a patient waitlist email.
 * - Declined and ClinicDeclinedAfterMeetAndGreet map to ClosedClinicDeclinedAfterMeetGreet and send patient email.
 * - PatientWithdrew and PatientAcceptedElsewhere are internal workflow closures and do not send patient email.
 */
export async function finalizeFamilyDoctorRequest(
  requestId: number,
  action: FinalizeAction,
  note?: string | null
): Promise<{
  success: boolean;
  action: FinalizeAction;
  targetStatus?: RequestStatus;
  message?: string;
}> {
  if (!Number.isFinite(requestId) || requestId <= 0) {
    throw new Error("Invalid requestId");
  }

  const a = String(action || "").trim() as FinalizeAction;
  if (!ALLOWED_FINALIZE_ACTIONS.includes(a)) {
    throw new Error("Invalid finalize action");
  }

  const cleanNote = String(note || "").trim();

  return requestJson<{
    success: boolean;
    action: FinalizeAction;
    targetStatus?: RequestStatus;
    message?: string;
  }>(`/api/portal/family-doctor/requests/${requestId}/finalize`, {
    method: "POST",
    body: JSON.stringify({
      action: a,
      ...(cleanNote ? { note: cleanNote } : {}),
    }),
  });
}